import React from 'react';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm1_u1_x4_adiabatik';

const M1_U1_X4_Adiabatik: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM1, canUploadImagesM1 } = useAuth();
  const { override, refresh } = useUnitOverride(PAGE_ID);

  // Textblöcke für TTS vorbereiten
  const textBlocks = React.useMemo(() => ([
    { id: 'block_intro', text: override?.texts?.['block_intro'] ?? 'Die adiabatische Funktion in einer Lüftungsanlage bezieht sich auf einen Kühlprozess ohne Wärmeaustausch mit der Umgebung. Typisch zur adiabatischen Kühlung der Zuluft eingesetzt.' },
    { id: 'block_prinzip', heading: 'Das Prinzip der adiabatischen Kühlung', text: override?.texts?.['block_prinzip'] ?? 'Adiabatische Kühlung nutzt Verdunstungskälte: Wasser verdunstet und entzieht der Luft Wärme. Keine externe Wärmezu-/-abfuhr; fühlbare Wärme der Luft liefert Energie. Temperatur sinkt, absoluter und relativer Feuchtegehalt steigen.' },
    { id: 'block_umsetzung', heading: 'Umsetzung in einer Lüftungsanlage', text: override?.texts?.['block_umsetzung'] ?? 'Direkt: Wasser verdunstet direkt in Zuluft. Indirekt: Abluft wird befeuchtet und kühlt Zuluft über Wärmetauscher ohne Feuchtezunahme. Kombiniert: Beide Verfahren für erhöhten Effekt.' },
    { id: 'block_vorteile', heading: 'Vorteile', text: override?.texts?.['block_vorteile'] ?? 'Energieeffizient, umweltfreundlich (kein Kältemittel), kostengünstig, kann Luftqualität verbessern.' },
    { id: 'block_nachteile', heading: 'Nachteile und Einschränkungen', text: override?.texts?.['block_nachteile'] ?? 'Anstieg Luftfeuchte (direkt), begrenzte Kühlleistung bei schwüler Außenluft, Wasserverbrauch, hoher Hygieneaufwand.' },
    { id: 'block_zusammenfassung', text: override?.texts?.['block_zusammenfassung'] ?? 'Adiabatik: energieeffiziente, umweltfreundliche Zuluftkühlung. Wahl direkt/indirekt abhängig von Feuchteanforderungen.' }
  ]), [override]);

  const fullPlainText = React.useMemo(() => {
    return textBlocks.map(b => `${b.heading ? b.heading + '. ' : ''}${b.text}`).join(' ');
  }, [textBlocks]);

  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canUploadImagesM1) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canUploadImagesM1]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    if (!canEditTextsM1) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh, canEditTextsM1]);

  const ttsButton = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));
  return React.createElement(
    UnitPageShell,
    { title: '1.1.4 Adiabatik', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        React.createElement(ZoomableImage, {
          src: (() => { const v = override?.images?.['img_0']; if (!v) return '/assets/drawable/wissen_bild_gross_rlt_anlage.jpg'; const s = String(v); return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`; })(),
          alt: 'RLT-Anlage mit adiabatischer Kühlung',
          label: 'Bild einfügen',
          onReplace: canUploadImagesM1 ? (file: File) => handleImageReplace('img_0', file) : undefined
        })
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        textBlocks.map((block) => React.createElement(EditableTextBlock, {
          key: block.id,
          pageId: PAGE_ID,
          blockId: block.id,
          heading: block.heading,
          body: block.text,
          onSave: (newBody: string) => handleTextSave(block.id, newBody)
        })),
        React.createElement(Box, { sx: { mt: 3 } },
          React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m1_u1_x99_quiz') }, 'Zum Quiz')
        )
      )
    )
  );
};

export default M1_U1_X4_Adiabatik;

